import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/pq79qyzz_84d55109-d543-4a1a-9205-51b665e4d9d0.webp";

const FOOTER_LANDING_LINKS = [
    ["/iv-therapy-san-diego-ca", "IV Therapy"],
    ["/im-shots-san-diego-ca", "IM Shots"],
    ["/neurotoxins-san-diego-ca", "Neurotoxins"],
    ["/facial-services-san-diego-ca", "Facials"],
    ["/microneedling-san-diego-ca", "Microneedling"],
    ["/medical-weight-loss-san-diego-ca", "Medical Weight Loss"],
    ["/peptide-therapy-san-diego-ca", "Peptide Therapy"],
];

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="bg-[#262422] text-[#F2EDE2]"
        >
            <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 pt-24 pb-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    <div className="lg:col-span-5">
                        <img loading="lazy" decoding="async" src={LOGO} alt="Zenith Wellness" className="h-20 w-auto" />
                        <p className="serif text-3xl md:text-4xl mt-8 leading-[1.15] text-[#F2EDE2]">
                            Holistic, modest, and<br />
                            subtle aesthetics in<br />
                            <span className="text-[#A89070] italic">San Diego.</span>
                        </p>
                        <div className="flex items-center gap-3 mt-10">
                            <a
                                href="https://www.instagram.com/zenithwellness_sd/"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-instagram"
                                className="w-11 h-11 rounded-full border border-[#A89070]/40 flex items-center justify-center hover:bg-[#262422] hover:text-[#A89070] transition"
                            >
                                <Instagram size={16} />
                            </a>
                            <a
                                href="https://www.facebook.com/people/Zenith-Wellness/61587655520941/"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-facebook"
                                className="w-11 h-11 rounded-full border border-[#A89070]/40 flex items-center justify-center hover:bg-[#262422] hover:text-[#A89070] transition"
                            >
                                <Facebook size={16} />
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-3">
                        <div className="eyebrow text-[#A89070]/70 mb-6">Explore</div>
                        <ul className="space-y-4 text-base">
                            <li><Link to="/services" className="hover:text-[#A89070]">Treatments</Link></li>
                            <li><Link to="/memberships" className="hover:text-[#A89070]">Memberships</Link></li>
                            <li><Link to="/about" className="hover:text-[#A89070]">About</Link></li>
                            <li><Link to="/contact" className="hover:text-[#A89070]">Contact</Link></li>
                            <li><Link to="/admin/login" className="opacity-50 hover:text-[#A89070]">Admin</Link></li>
                        </ul>

                        <div className="eyebrow text-[#A89070]/70 mt-10 mb-4">San Diego Treatments</div>
                        <ul className="space-y-3 text-sm" data-testid="footer-landing-links">
                            {FOOTER_LANDING_LINKS.map(([to, label]) => (
                                <li key={to}><Link to={to} className="hover:text-[#A89070]">{label}</Link></li>
                            ))}
                        </ul>

                        <div className="eyebrow text-[#A89070]/70 mt-10 mb-4">Learn</div>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/blog" data-testid="footer-blog-link" className="hover:text-[#A89070]">Blog</Link></li>
                            <li><Link to="/education" className="hover:text-[#A89070]">Education</Link></li>
                            <li><Link to="/faqs" className="hover:text-[#A89070]">FAQs</Link></li>
                            <li><Link to="/financing" className="hover:text-[#A89070]">Financing</Link></li>
                            <li><Link to="/hair-restoration" className="hover:text-[#A89070]">Hair Restoration</Link></li>
                            <li>
                                <a
                                    href="/api/static/flyers/botox-hyperhidrosis.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-flyer-botox"
                                    className="hover:text-[#A89070] inline-flex items-center gap-2"
                                >
                                    Botox for Underarm Sweating
                                    <span className="text-[10px] text-[#A89070]/60">→</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div className="lg:col-span-4">
                        <div className="eyebrow text-[#A89070]/70 mb-6">Visit</div>
                        <address className="not-italic space-y-2 text-base leading-relaxed">
                            <div>3727 Camino Del Rio S, Suite 210</div>
                            <div>San Diego, CA 92108</div>
                            <div className="pt-2">
                                <a href="tel:8589054117" className="block hover:text-[#A89070]">858 905 4117</a>
                                <a href="mailto:info@zenithwellness-sd.com" className="block hover:text-[#A89070]">info@zenithwellness-sd.com</a>
                            </div>
                        </address>

                        <div className="eyebrow text-[#A89070]/70 mt-8 mb-4">Hours</div>
                        <div className="text-sm space-y-1.5 text-[#F2EDE2]/80">
                            <div>Mon · Wed · Thu · Sat — 9am–3pm</div>
                            <div>Friday — By appointment</div>
                            <div>Tue & Sun — Closed</div>
                        </div>
                    </div>
                </div>

                <hr className="hr-gold my-12" />

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#F2EDE2]/50">
                    <div>© {new Date().getFullYear()} Zenith Wellness — All rights reserved</div>
                    <div className="tracking-[0.3em] uppercase">San Diego · California</div>
                </div>

                <div className="mt-6 text-center text-[11px] tracking-[0.22em] uppercase text-[#F2EDE2]/40" data-testid="footer-seo-credit">
                    Website &amp; SEO managed by{" "}
                    <a
                        href="https://webnehtech.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#A89070] hover:text-[#F2EDE2] transition"
                    >
                        WebNehTech.com
                    </a>
                </div>
            </div>
        </footer>
    );
}
